import { motion } from "framer-motion";

/**
 * AI & Agents background — an AI chat interface with streaming text,
 * tool-use indicators, and a neural network accent in the background.
 * Immediately recognizable as "AI" through the conversational UI pattern.
 */

const streamingChars = "Analyzing data and generating insights based on your request...";

const nodes = [
  { x: 8, y: 15 }, { x: 25, y: 8 }, { x: 42, y: 18 },
  { x: 60, y: 10 }, { x: 78, y: 16 }, { x: 92, y: 8 },
  { x: 15, y: 30 }, { x: 50, y: 28 }, { x: 85, y: 32 },
];

const connections = [
  [0, 1], [1, 2], [2, 3], [3, 4], [4, 5],
  [0, 6], [2, 7], [4, 8], [6, 7], [7, 8], [1, 7],
];

export const NeuralNetworkBg = () => (
  <div className="absolute inset-0 overflow-hidden">
    {/* Subtle neural network in top background */}
    <svg className="absolute top-0 left-0 w-full h-[40%] opacity-40" viewBox="0 0 100 40" preserveAspectRatio="xMidYMid slice">
      {connections.map(([a, b], i) => (
        <g key={`c-${i}`}>
          <line
            x1={nodes[a].x} y1={nodes[a].y}
            x2={nodes[b].x} y2={nodes[b].y}
            stroke="hsl(239 84% 67% / 0.2)"
            strokeWidth="0.3"
          />
          <circle r="0.5" fill="hsl(239 84% 67% / 0.6)">
            <animateMotion
              dur={`${2.5 + (i % 3)}s`}
              repeatCount="indefinite"
              begin={`${i * 0.5}s`}
              path={`M${nodes[a].x},${nodes[a].y} L${nodes[b].x},${nodes[b].y}`}
            />
          </circle>
        </g>
      ))}
      {nodes.map((node, i) => (
        <motion.circle
          key={`n-${i}`}
          cx={node.x} cy={node.y} r="1"
          fill="hsl(239 84% 67% / 0.5)"
          animate={{ opacity: [0.3, 0.8, 0.3], r: [0.8, 1.3, 0.8] }}
          transition={{ duration: 2.5 + (i % 3), repeat: Infinity, delay: i * 0.2 }}
        />
      ))}
    </svg>

    {/* Chat interface */}
    <div className="absolute inset-0 flex flex-col px-4 pt-[38%] pb-3 gap-[6px]">
      {/* User message */}
      <motion.div
        className="self-end max-w-[70%] rounded-xl rounded-br-sm px-[7px] py-[5px]"
        style={{ backgroundColor: "hsl(239 84% 67% / 0.25)" }}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.4 }}
      >
        <div className="flex flex-col gap-[2px]">
          <div className="h-[3px] w-[85%] rounded-full bg-white/20" />
          <div className="h-[3px] w-[60%] rounded-full bg-white/15" />
        </div>
      </motion.div>

      {/* AI response with streaming effect */}
      <motion.div
        className="self-start max-w-[80%] flex gap-[5px]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8, duration: 0.3 }}
      >
        {/* AI avatar */}
        <motion.div
          className="w-[10px] h-[10px] rounded-full shrink-0 flex items-center justify-center mt-[2px]"
          style={{ backgroundColor: "hsl(239 84% 67% / 0.3)", border: "1px solid hsl(239 84% 67% / 0.4)" }}
          animate={{ boxShadow: ["0 0 0px hsl(239 84% 67% / 0)", "0 0 6px hsl(239 84% 67% / 0.3)", "0 0 0px hsl(239 84% 67% / 0)"] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <span className="text-[5px] font-bold text-indigo-300/80">AI</span>
        </motion.div>

        <div className="rounded-xl rounded-bl-sm px-[7px] py-[5px] border border-white/[0.06]"
          style={{ backgroundColor: "hsl(0 0% 100% / 0.04)" }}
        >
          {/* Streaming text lines */}
          <div className="flex flex-col gap-[3px]">
            {[0.9, 0.75, 0.85, 0.6].map((w, i) => (
              <motion.div
                key={i}
                className="h-[3px] rounded-full bg-white/12"
                initial={{ width: 0 }}
                animate={{ width: `${w * 100}%` }}
                transition={{ delay: 1 + i * 0.3, duration: 0.5, ease: "easeOut" }}
                style={{ maxWidth: 80 }}
              />
            ))}
            {/* Blinking cursor */}
            <motion.div
              className="h-[4px] w-[2px] rounded-[0.5px] bg-indigo-400/60 mt-[1px]"
              animate={{ opacity: [1, 0, 1] }}
              transition={{ duration: 0.8, repeat: Infinity }}
            />
          </div>
        </div>
      </motion.div>

      {/* Tool use indicator */}
      <motion.div
        className="self-start ml-[15px] flex items-center gap-[4px] px-[6px] py-[3px] rounded-md border border-white/[0.06]"
        style={{ backgroundColor: "hsl(150 60% 50% / 0.06)" }}
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: [0, 1, 1, 0], height: "auto" }}
        transition={{ delay: 2.5, duration: 4, repeat: Infinity, repeatDelay: 6 }}
      >
        <motion.div
          className="w-[4px] h-[4px] rounded-full bg-emerald-400/60"
          animate={{ scale: [1, 1.4, 1] }}
          transition={{ duration: 0.8, repeat: Infinity }}
        />
        <span className="text-[4px] text-emerald-300/40 font-mono">Running analysis...</span>
        <motion.div className="flex gap-[2px]">
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              className="w-[2px] h-[2px] rounded-full bg-emerald-400/40"
              animate={{ opacity: [0.2, 1, 0.2] }}
              transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
            />
          ))}
        </motion.div>
      </motion.div>

      {/* Input bar at bottom */}
      <div className="mt-auto flex items-center gap-[4px]">
        <div className="flex-1 h-[14px] rounded-lg border border-white/[0.1] bg-white/[0.03] flex items-center px-[6px]">
          <motion.div
            className="h-[3px] rounded-full bg-white/10"
            animate={{ width: ["0%", "40%", "40%", "0%"] }}
            transition={{ duration: 6, repeat: Infinity, repeatDelay: 3 }}
          />
        </div>
        <motion.div
          className="w-[14px] h-[14px] rounded-lg flex items-center justify-center"
          style={{ backgroundColor: "hsl(239 84% 67% / 0.35)" }}
          animate={{ scale: [1, 1.08, 1] }}
          transition={{ duration: 2, repeat: Infinity, repeatDelay: 4 }}
        >
          <div className="w-0 h-0 border-l-[3px] border-l-white/40 border-y-[2px] border-y-transparent ml-[1px]" />
        </motion.div>
      </div>
    </div>
  </div>
);
